import { Target, Eye, Award, Users } from 'lucide-react';

export default function About() {
    const milestones = [
        { year: '2020', event: 'Mojo Money founded with a vision to simplify remittances' },
        { year: '2021', event: 'Reached 10,000 satisfied customers' },
        { year: '2022', event: 'Expanded to 50+ partner banks across Ethiopia' },
        { year: '2023', event: 'Processed over $100 million in transactions' },
        { year: '2024', event: 'Launched mobile app with enhanced features' },
        { year: '2025', event: 'Serving 100,000+ users worldwide' }
    ];

    const values = [
        {
            icon: Target,
            title: 'Trust & Security',
            description: 'We prioritize the security of your money with bank-level encryption and compliance with international standards.'
        },
        {
            icon: Users,
            title: 'Customer First',
            description: 'Our customers are at the heart of everything we do. We listen, adapt, and deliver exceptional service.'
        },
        {
            icon: Award,
            title: 'Excellence',
            description: 'We strive for excellence in every transaction, every interaction, and every innovation we bring to market.'
        },
        {
            icon: Eye,
            title: 'Transparency',
            description: 'Clear pricing, honest communication, and no hidden fees. We believe in building trust through transparency.'
        }
    ];

    return (
        <div className="min-h-screen pt-24 pb-20">
            <section className="bg-gradient-to-br from-emerald-800 to-emerald-900 text-white py-20">
                <div className="container mx-auto px-4">
                    <div className="max-w-3xl mx-auto text-center">
                        <h1 className="text-5xl font-bold mb-6">
                            About Mojo Money
                        </h1>
                        <p className="text-xl text-emerald-50">
                            Connecting Ethiopian diaspora with their homeland through secure, fast, and reliable money transfer services.
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-20 bg-white">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-16">
                            <div>
                                <h2 className="text-4xl font-bold text-emerald-900 mb-6">Our Mission</h2>
                                <p className="text-lg text-gray-600 leading-relaxed mb-4">
                                    To provide the Ethiopian diaspora community with the most secure, efficient, and affordable way to send money home and support their loved ones.
                                </p>
                                <p className="text-lg text-gray-600 leading-relaxed">
                                    We believe that money transfer should be simple, transparent, and accessible to everyone, regardless of where they are in the world.
                                </p>
                            </div>
                            <div>
                                <img
                                    src="https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=800"
                                    alt="Our Mission"
                                    className="rounded-xl shadow-2xl"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                            <div className="order-2 md:order-1">
                                <img
                                    src="https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=800"
                                    alt="Our Vision"
                                    className="rounded-xl shadow-2xl"
                                />
                            </div>
                            <div className="order-1 md:order-2">
                                <h2 className="text-4xl font-bold text-emerald-900 mb-6">Our Vision</h2>
                                <p className="text-lg text-gray-600 leading-relaxed mb-4">
                                    To become the leading financial bridge between the Ethiopian diaspora and their homeland, fostering economic growth and family connections.
                                </p>
                                <p className="text-lg text-gray-600 leading-relaxed">
                                    We envision a future where distance is no barrier to supporting your family and contributing to your community.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                    <h2 className="text-4xl font-bold text-emerald-900 mb-12 text-center">Our Values</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
                        {values.map((value, index) => {
                            const Icon = value.icon;
                            return (
                                <div key={index} className="bg-white p-8 rounded-xl shadow-lg text-center hover:shadow-xl transition transform hover:scale-105">
                                    <div className="bg-emerald-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Icon className="w-8 h-8 text-emerald-800" />
                                    </div>
                                    <h3 className="text-xl font-bold text-emerald-900 mb-3">{value.title}</h3>
                                    <p className="text-gray-600">{value.description}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="py-20 bg-white">
                <div className="container mx-auto px-4">
                    <h2 className="text-4xl font-bold text-emerald-900 mb-12 text-center">Our Journey</h2>
                    <div className="max-w-4xl mx-auto">
                        <div className="space-y-8">
                            {milestones.map((milestone, index) => (
                                <div key={index} className="flex items-center gap-6">
                                    <div className="bg-emerald-800 text-white px-6 py-3 rounded-lg font-bold text-xl min-w-[100px] text-center">
                                        {milestone.year}
                                    </div>
                                    <div className="flex-1 bg-gray-50 p-6 rounded-lg shadow-md">
                                        <p className="text-lg text-gray-700">{milestone.event}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-20 bg-gradient-to-br from-emerald-800 to-emerald-900 text-white">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-4xl font-bold mb-6">Join Our Growing Community</h2>
                        <p className="text-xl text-emerald-50 mb-8">
                            Become part of a trusted network that's making a real difference in people's lives every day.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a
                                href="/contact"
                                className="bg-yellow-500 text-emerald-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-yellow-400 transition transform hover:scale-105"
                            >
                                Get Started Today
                            </a>
                            <a
                                href="/exchange-rates"
                                className="bg-white text-emerald-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-emerald-50 transition transform hover:scale-105"
                            >
                                View Exchange Rates
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
